// src/pages/FacturacionPresupuestos/SectionFontSizeControl.js
import React, { useState } from 'react';
import { FaTextHeight, FaRedo, FaFont } from 'react-icons/fa';
import { 
    SECCIONES_DOCUMENTO, 
    obtenerTamanoSeccion, 
    obtenerTamanoTituloSeccion,
    aplicarTamanoSeccion, 
    cambiarTamanoDelta, 
    restablecerTamanoSeccion,
    DELTA_TITULO_PUNTOS
} from './tamanoTextoUtils';
import './SectionFontSizeControl.css';

/**
 * Componente unificado para control de tamaño de fuente:
 * - mode="compact": Un solo control de incremento/decremento por sección,
 *   con escala proporcional automática (+4 puntos para el título de la sección).
 * - mode="bar": Barra con selector desplegable de sección y stepper numérico.
 */
const SectionFontSizeControl = ({
    documento,
    setDocumento,
    mode = 'bar',
    seccionId = 'global'
}) => {
    const [seccionSeleccionada, setSeccionSeleccionada] = useState(seccionId);

    // En modo compacto, usamos la seccionId provista como prop
    const idActivo = mode === 'compact' ? seccionId : seccionSeleccionada;
    const tamanoActual = obtenerTamanoSeccion(documento, idActivo);
    const tamanoTitulo = obtenerTamanoTituloSeccion(documento, idActivo);

    const handleInputChange = (e) => {
        const val = parseInt(e.target.value, 10);
        if (!isNaN(val)) {
            aplicarTamanoSeccion(setDocumento, idActivo, val);
        }
    };

    // Modo Compacto (Para cabecera de tarjeta de cada sección)
    if (mode === 'compact') {
        return (
            <div 
                className="section-font-control-compact" 
                title={`Tamaño proporcional: Texto ${tamanoActual} pt | Título ${tamanoTitulo} pt (+${DELTA_TITULO_PUNTOS} pt)`}
            >
                <span className="compact-label">
                    <FaFont style={{ fontSize: '10px' }} /> Tamaño:
                </span>
                <div className="section-font-stepper">
                    <button
                        type="button"
                        className="section-font-step-btn"
                        onClick={() => cambiarTamanoDelta(setDocumento, idActivo, tamanoActual, -1)}
                        title="Disminuir tamaño de la sección (texto y título proporcional)"
                    >
                        −
                    </button>
                    <input
                        type="number"
                        className="section-font-num-input"
                        value={tamanoActual}
                        onChange={handleInputChange}
                        min={7}
                        max={26}
                    />
                    <button
                        type="button"
                        className="section-font-step-btn"
                        onClick={() => cambiarTamanoDelta(setDocumento, idActivo, tamanoActual, 1)}
                        title="Aumentar tamaño de la sección (texto y título proporcional)"
                    >
                        +
                    </button>
                </div>
                <span className="compact-proporcional-pill" title={`El título escala automáticamente a ${tamanoTitulo} pt`}>
                    +{DELTA_TITULO_PUNTOS} Título
                </span>
            </div>
        );
    }

    // Modo Barra (Completo para Toolbar superior y Sidebar)
    return (
        <div className="section-font-control-bar">
            <div className="section-font-control-left">
                <span className="section-font-label">
                    <FaTextHeight style={{ color: '#2b5329' }} /> Tamaño de Texto por Sección:
                </span>

                {/* Selector de Sección */}
                <select
                    className="section-font-select"
                    value={seccionSeleccionada}
                    onChange={(e) => setSeccionSeleccionada(e.target.value)}
                    title="Selecciona la sección a la que deseas cambiar el tamaño de fuente"
                >
                    {SECCIONES_DOCUMENTO.map(sec => (
                        <option key={sec.id} value={sec.id}>
                            {sec.nombre} ({obtenerTamanoSeccion(documento, sec.id)} pt)
                        </option>
                    ))}
                </select>

                {/* Stepper Numérico Unificado */}
                <div className="section-font-stepper">
                    <button
                        type="button"
                        className="section-font-step-btn"
                        onClick={() => cambiarTamanoDelta(setDocumento, idActivo, tamanoActual, -1)}
                        title="Disminuir tamaño de texto de la sección"
                    >
                        −
                    </button>
                    <input
                        type="number"
                        className="section-font-num-input"
                        value={tamanoActual}
                        onChange={handleInputChange}
                        min={7}
                        max={26}
                    />
                    <button
                        type="button"
                        className="section-font-step-btn"
                        onClick={() => cambiarTamanoDelta(setDocumento, idActivo, tamanoActual, 1)}
                        title="Aumentar tamaño de texto de la sección"
                    >
                        +
                    </button>
                </div>
                <span className="section-font-unit">pt</span>

                <span className="section-font-proporcional-badge" title="Separación armónica garantizada">
                    Título: {tamanoTitulo} pt (+{DELTA_TITULO_PUNTOS} pt)
                </span>

                <button
                    type="button"
                    className="section-font-reset-btn"
                    onClick={() => restablecerTamanoSeccion(setDocumento, idActivo)}
                    title="Restablecer tamaño predeterminado de esta sección"
                >
                    <FaRedo style={{ fontSize: '9px' }} /> Restablecer
                </button>
            </div>

            <div className="section-font-hint">
                Escala armónica: texto a {tamanoActual} pt y títulos a {tamanoTitulo} pt
            </div>
        </div>
    );
};

export default SectionFontSizeControl;
